import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ProductCard } from '../components/product/ProductCard';
import { useProducts, useCategories, useSubcategories } from '../hooks/useData';

export const CategoryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { categories } = useCategories();
  const { subcategories } = useSubcategories(id);
  const { products, loading } = useProducts({ category_id: id });

  const category = categories.find(cat => cat.id === id);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-3xl font-bold mb-4">Category not found</h2>
          <Button onClick={() => navigate('/categories')}>Back to Categories</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="container-custom">
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => navigate('/categories')}
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Categories
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-foreground mb-4">
            {category.name}
          </h1>
          {category.description && (
            <p className="text-lg text-muted-foreground max-w-3xl">
              {category.description}
            </p>
          )}
        </motion.div>

        {subcategories.length > 0 && (
          <div className="mb-8">
            <h2 className="font-serif text-2xl font-semibold mb-4">Subcategories</h2>
            <div className="flex flex-wrap gap-3">
              {subcategories.map(sub => (
                <Button
                  key={sub.id}
                  variant="outline"
                  onClick={() => navigate(`/shop?category=${id}&subcategory=${sub.id}`)}
                >
                  {sub.name}
                </Button>
              ))}
            </div>
          </div>
        )}

        {products.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <ProductCard product={product} />
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-muted-foreground text-lg">No products available in this category yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};